import { atom, useAtom } from 'jotai'

interface FaxToastState {
  open?: boolean
}

interface MessagingToastState {
  open?: boolean
  messagingStatus?: string
  messagingError?: string
  clickToCallStatus?: string
  threadUpdateStatus?: string
}

interface ToastStates {
  faxToast: FaxToastState
  messagingToast: MessagingToastState
}

interface UseToastStateResponse {
  toastState: ToastStates
  faxToastState: [
    FaxToastState,
    (
      newFaxToastState:
        FaxToastState | ((currentState: FaxToastState) => FaxToastState),
    ) => void,
  ]
  messagingToast: [
    MessagingToastState,
    (
      newMessagingToastState:
        | MessagingToastState
        | ((currentState: MessagingToastState) => MessagingToastState),
    ) => void,
  ]
  isAnyToastOpen: boolean
}

const toastStateAtom = atom<ToastStates>({
  faxToast: {
    open: false,
  },
  messagingToast: {
    open: false,
  },
})

const useToastState = (): UseToastStateResponse => {
  const [toastState, setToastState] = useAtom(toastStateAtom)

  const setFaxToastState = (
    newFaxToastState:
      FaxToastState | ((currentState: FaxToastState) => FaxToastState),
  ) => {
    const newState: FaxToastState =
      typeof newFaxToastState === 'function'
        ? newFaxToastState(toastState.faxToast)
        : newFaxToastState

    setToastState({
      ...toastState,
      faxToast: newState,
    })
  }

  const setMessagingToastState = (
    newMessagingToastState:
      | MessagingToastState
      | ((currentState: MessagingToastState) => MessagingToastState),
  ) => {
    const newState: MessagingToastState =
      typeof newMessagingToastState === 'function'
        ? newMessagingToastState(toastState.messagingToast)
        : newMessagingToastState

    setToastState({
      ...toastState,
      messagingToast: {
        ...newState,
      },
    })
  }

  const isAnyToastOpen = (): boolean => {
    return (
      Boolean(toastState.faxToast.open) ||
      Boolean(toastState.messagingToast.open)
    )
  }

  return {
    toastState,
    faxToastState: [toastState.faxToast, setFaxToastState],
    messagingToast: [toastState.messagingToast, setMessagingToastState],
    isAnyToastOpen: isAnyToastOpen(),
  }
}

export type { FaxToastState, ToastStates, UseToastStateResponse }
export { useToastState }
